import { AppShell, LanguageSelector, type SeoConfig } from '@kkf/design'
import { LOGO_WIDE, LOGO_WIDE_WHITE } from '../data/shell'
import { useShellContent } from '../hooks/useShellContent'

export interface SiteShellProps {
  seo: SeoConfig
  /** Class for the `<main>` wrapper — one route needs it. See `App`. */
  mainClassName?: string
}

/**
 * The Foundation site's chrome, in the reader's language.
 *
 * A component rather than the `<AppShell {...SHELL} />` that used to sit in `App`'s route
 * table: `App` renders `LocaleProvider`, and a component cannot read a context it provides
 * itself. The nav, the footer and the header's button are all copy.
 *
 * **No search opener (2026-09-19).** `search: false` takes the magnifier out of the bar and
 * stops `SearchModal` being mounted behind it. The modal's field was never wired to
 * anything — `onSearch` is unset on this app — so what a visitor got for clicking it was an
 * overlay, a field, and nothing on submit. A control that promises a search the site cannot
 * perform is worse than no control. The garage and the resort still carry theirs.
 *
 * The **language chooser** goes in `header.actions`, the slot `Header` keeps for an extra
 * control in the bar. Without it the catalogue is unreachable: a reader could only get
 * Mongolian by having asked their browser for it, and a translation nobody can choose is
 * one nobody reads. This is the Foundation site — the front door — so it is also where
 * most visitors will meet the switch at all.
 */
export function SiteShell({ seo, mainClassName }: SiteShellProps) {
  const { nav, headerCta, footer, sidebar, bottomNav } = useShellContent()

  return (
    <AppShell
      logo={LOGO_WIDE}
      logoPale={LOGO_WIDE_WHITE}
      nav={nav}
      sidebar={sidebar}
      header={{
        cta: headerCta,
        actions: <LanguageSelector />,
        search: false,
        /*
         * **Every route but the home page.** The pages lost `Breadcrumb`'s dark banner in
         * three passes — `/about`, `/donations` and the legal pair on 2026-09-19, `/blog`,
         * `/blog/*` and `/contact` on the 20th and 21st, and the remaining eleven on the
         * 21st — and each now opens on its own pale content. The bar floats over the first
         * section and its links are white for a photograph, so each has to darken; each
         * also carries `.ul-plain-page-top`, which reserves the bar's height and washes the
         * ground under it.
         *
         * **Still a list, not `plain: true`.** An app-wide boolean was tried on the garage
         * and was wrong within a day: one route there still opens on a dark photographic
         * banner, and "this whole app is on light" put ink navy on near-black. "Is the bar
         * over something pale" is a per-route fact even when the answer is currently the
         * same for seventeen routes out of eighteen — and the day a page here gets a banner
         * back, the fix is deleting one line rather than discovering why the nav went
         * white. `/` is the one that makes that claim, and the route suite asserts it.
         *
         * `/privacy` and `/terms` are the ones to watch: `LegalPage` is shared by all five
         * apps, so one edit to it turns the nav white-on-white on ten pages at once.
         *
         * A `/*` suffix is a prefix match and is the only way to cover a `:slug` route.
         */
        plainRoutes: [
          '/about',
          '/services',
          '/services/*',
          '/projects',
          '/projects/*',
          '/team',
          '/team/*',
          '/pricing',
          '/faq',
          '/donations',
          '/donations/*',
          '/events',
          '/events/*',
          '/blog',
          '/blog/*',
          '/contact',
          '/privacy',
          '/terms',
        ],
      }}
      footer={footer}
      bottomNav={bottomNav}
      seo={seo}
      {...(mainClassName ? { mainClassName } : {})}
    />
  )
}
